/**
 * Simple Toast Service using Bootstrap Toast Component
 */
class ToastService {
  constructor() {
    this.toastContainer = null;
    this.toastIdCounter = 0;
    this.initializeContainer();
  }

  /**
   * Initialize the toast container
   */
  initializeContainer() {
    // Create toast container if it doesn't exist
    if (!document.getElementById('toast-container')) {
      const container = document.createElement('div');
      container.id = 'toast-container';
      container.className = 'toast-container position-fixed top-0 end-0 p-3';
      container.style.zIndex = '9999';
      document.body.appendChild(container);
    }
    this.toastContainer = document.getElementById('toast-container');
  }

  /**
   * Check if Bootstrap is available
   */
  isBootstrapAvailable() {
    return typeof window !== 'undefined' && 
           window.bootstrap && 
           window.bootstrap.Toast;
  }

  /**
   * Show a fallback alert if Bootstrap is not available
   */
  showFallbackAlert(message, type) {
    const alertMessages = {
      success: '✅ Success: ',
      error: '❌ Error: ',
      warning: '⚠️ Warning: ',
      info: 'ℹ️ Info: '
    };
    
    const prefix = alertMessages[type] || alertMessages.info;
    alert(prefix + message);
  }

  /**
   * Show a toast notification
   * @param {string} message - The message to display
   * @param {string} type - The type of toast (success, error, warning, info)
   * @param {number} duration - Duration in milliseconds (default: 5000)
   */
  show(message, type = 'info', duration = 5000) {
    // Fallback to alert if Bootstrap is not available
    if (!this.isBootstrapAvailable()) {
      console.warn('Bootstrap Toast not available, using fallback alert');
      this.showFallbackAlert(message, type);
      return;
    }

    const toastId = `toast-${this.toastIdCounter++}`;
    
    // Create toast element
    const toastElement = document.createElement('div');
    toastElement.id = toastId;
    toastElement.className = `toast align-items-center text-white border-0 ${this.getToastClass(type)}`;
    toastElement.setAttribute('role', 'alert');
    toastElement.setAttribute('aria-live', 'assertive');
    toastElement.setAttribute('aria-atomic', 'true');
    toastElement.setAttribute('data-bs-delay', duration.toString());

    // Create toast content
    toastElement.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">
          <i class="bi ${this.getToastIcon(type)} me-2"></i>
          ${message}
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    `;

    // Add to container
    this.toastContainer.appendChild(toastElement);

    try {
      // Initialize Bootstrap toast
      const bsToast = new window.bootstrap.Toast(toastElement, {
        autohide: true,
        delay: duration
      });

      // Show toast
      bsToast.show();

      // Remove element after it's hidden
      toastElement.addEventListener('hidden.bs.toast', () => {
        toastElement.remove();
      });
    } catch (error) {
      console.error('Failed to create Bootstrap toast:', error);
      // Remove the element if toast creation fails
      toastElement.remove();
      // Show fallback alert
      this.showFallbackAlert(message, type);
    }

    return toastId;
  }

  /**
   * Get Bootstrap class for toast type
   * @param {string} type - Toast type
   * @returns {string} Bootstrap class
   */
  getToastClass(type) {
    switch (type) {
      case 'success':
        return 'bg-success';
      case 'error':
        return 'bg-danger';
      case 'warning':
        return 'bg-warning';
      case 'info':
      default:
        return 'bg-primary';
    }
  }

  /**
   * Get Bootstrap icon for toast type
   * @param {string} type - Toast type
   * @returns {string} Bootstrap icon class
   */
  getToastIcon(type) {
    switch (type) {
      case 'success':
        return 'bi-check-circle';
      case 'error':
        return 'bi-exclamation-circle';
      case 'warning':
        return 'bi-exclamation-triangle';
      case 'info':
      default:
        return 'bi-info-circle';
    }
  }

  /**
   * Show success toast
   * @param {string} message - Success message
   * @param {number} duration - Duration in milliseconds
   */
  success(message, duration = 5000) {
    return this.show(message, 'success', duration);
  }

  /**
   * Show error toast
   * @param {string} message - Error message
   * @param {number} duration - Duration in milliseconds
   */
  error(message, duration = 5000) {
    return this.show(message, 'error', duration);
  }

  /**
   * Show warning toast
   * @param {string} message - Warning message
   * @param {number} duration - Duration in milliseconds
   */
  warning(message, duration = 5000) {
    return this.show(message, 'warning', duration);
  }

  /**
   * Show info toast
   * @param {string} message - Info message
   * @param {number} duration - Duration in milliseconds
   */
  info(message, duration = 5000) {
    return this.show(message, 'info', duration);
  }

  /**
   * Clear all toasts
   */
  clearAll() {
    if (this.toastContainer) {
      this.toastContainer.innerHTML = '';
    }
  }
}

// Export singleton instance
export default new ToastService(); 